import { normalizeAdminPermission } from "@/utils/defaultLoggedInRedirect";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** RFC 4122 v4 id, created on the client so a retried POST is idempotent. */
export function generateCommentId() {
  const c = window.crypto;
  if (typeof c?.randomUUID === "function") return c.randomUUID();
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  const part = (from, to) => hex.slice(from, to);
  return `${part(0, 8)}-${part(8, 12)}-${part(12, 16)}-${part(16, 20)}-${part(20)}`;
}

/** "Oct 10, 2026" — the standard exIQtive date format. */
export function formatCommentDate(value) {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** "Just now", then minutes/hours, falling back to the date once it is a day old. */
export function formatCommentTime(value, now = Date.now()) {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  const minutes = Math.floor((now - d.getTime()) / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  return formatCommentDate(value);
}

/** The signed-in viewer: their Access id and whether they are an account admin. */
export function getCommentViewer() {
  let permission = {};
  try {
    permission = JSON.parse(localStorage.getItem("accessPermission") || "{}") || {};
  } catch (error) {
    permission = {};
  }
  const accessId = permission.id == null || permission.id === "" ? null : String(permission.id);
  return { accessId, isAdmin: normalizeAdminPermission(permission).hasElevatedNav };
}

export const isIqComment = (comment) =>
  Boolean(comment?.is_iq_author) || comment?.author?.type === "iq";

export function isOwnComment(comment, viewer) {
  if (isIqComment(comment)) return false;
  const authorId = comment?.author?.id;
  if (authorId == null || !viewer?.accessId) return false;
  return String(authorId) === viewer.accessId;
}

/** Replies the user can see: soft-deleted replies are hidden. */
export const liveReplies = (replies) => (replies || []).filter((r) => !r.deleted_at);

/** Ids of the comments a thread currently shows (collapsed replies are not visible). */
export function visibleCommentIds(root, replies, { showReplies = true } = {}) {
  const ids = [];
  if (root && !root.deleted_at) ids.push(root.id);
  if (showReplies) liveReplies(replies).forEach((r) => ids.push(r.id));
  return ids;
}

/** Identity of a feed entry: comments and events come wrapped in `{ kind, comment|event }`. */
export function feedItemKey(item) {
  if (item?.kind === "comment") return `comment:${item.comment?.id}`;
  if (item?.kind === "event") return `event:${item.event?.id}`;
  return item?.id;
}
